import type { Card, SeatView } from '@tctp/protocol';
import { type ReactNode, useRef } from 'react';
import type { Highlight } from '../bestFive';
import { DEAL_STEP_MS, type Point } from '../stage';
import { PlayingCard } from './PlayingCard';


export function statusCaption(v: SeatView): string {
  if (!v.connected) return 'OFFLINE';
  if (v.away) return 'AWAY';
  if (v.busted) return 'BUSTED';
  if (v.leaving) return 'LEAVING';
  if (v.inHand && v.folded) return 'FOLD';
  if (v.inHand && v.allIn) return 'ALL IN';
  if (!v.inHand) return 'WAITING';
  return '';
}

/** Two letters for the avatar: the initials of a two-word name, else the first two letters. */
export function initials(nickname: string): string {
  const words = nickname.trim().split(/\s+/).filter(Boolean);
  const two = words.length >= 2 ? words[0]![0]! + words[1]![0]! : nickname.trim().slice(0, 2);
  return two.toUpperCase();
}

/** The cards still in a player's hand: all three, or two once their discard is chosen (the server sets `keepers` only after the reveal). */
function inHand(v: SeatView): Card[] {
  return v.discard !== null ? v.holeCards!.filter(c => c !== v.discard) : v.holeCards!;
}

/** Deal-in delay for the i-th card of a seat, dealing one card per seat per round. */
export function dealDelay(dealIndex: number | null, dealCount: number, cardIndex: number): number | undefined {
  return dealIndex === null ? undefined : (cardIndex * dealCount + dealIndex) * DEAL_STEP_MS;
}

export function SeatBox({ view, isMe, acting, remainingMs, totalMs, extraTime = false, winner, won = null, equity = null, highlight = null, showdown, tags, handId, marked = null, onToggleCard, dealIndex = null, dealCount = 0, dealFrom, portrait = false }: {
  view: SeatView; isMe: boolean; acting: boolean; remainingMs: number | null; totalMs: number; extraTime?: boolean; winner: boolean; won?: number | null; equity?: number | null; highlight?: Highlight | null; showdown: boolean;
  /** Hand names astride the pill's bottom edge: one line, or `#1 …` and `#2 …` when the board was run twice. */
  tags: string[]; handId: string | null; marked?: Card | null; onToggleCard?: (card: Card) => void;
  dealIndex?: number | null; dealCount?: number; dealFrom?: Point; portrait?: boolean;
}) {
  const caption = statusCaption(view);
  const live = view.inHand && !view.folded;
  const chosen = view.discard !== null || view.discardLocked;   // two cards in hand once the discard is locked, its identity shown only after the reveal
  const fraction = acting && remainingMs !== null && totalMs > 0 ? Math.max(0, Math.min(1, remainingMs / totalMs)) : null;
  const cls = ['seatbox', isMe ? 'me' : '', acting ? 'acting' : '', winner ? 'winner' : '', view.inHand && view.folded ? 'folded' : '', view.isButton ? 'button' : ''].filter(Boolean).join(' ');
  const key = (c: number | string): string => `${handId ?? 'h'}:${c}`;
  // Another seat's two kept cards keep index keys in every face state, so a reveal flips the element in place instead of remounting it;
  // a shown card keeps the slot it first appeared in (showing one card and then the other leaves the first where it was).
  const slots = useRef<{ handId: string | null; cards: Card[] }>({ handId: null, cards: [] });
  if (slots.current.handId !== handId) slots.current = { handId, cards: [] };
  for (const c of view.shown) if (!slots.current.cards.includes(c)) slots.current.cards.push(c);

  // A winner's cards lift 16px at showdown. Labels above the cards move once any card lifts; labels below move only when every
  // card lifts, since a card left down still needs its space.
  const faceUp = [...new Set([...(view.keepers ?? []), ...view.shown, ...slots.current.cards])];
  const liftedAny = highlight !== null && faceUp.some(c => highlight.used.has(c));
  const liftedAll = liftedAny && faceUp.every(c => highlight!.used.has(c));

  let cards: ReactNode = null;
  if (isMe && view.inHand && view.holeCards) {
    const picking = live && !view.discardLocked && onToggleCard !== undefined;
    const shown = view.keepers ?? inHand(view);
    cards = shown.map((c, i) => (
      <PlayingCard key={key(c)} card={c} size={portrait ? 'lg' : 'md'} marked={marked === c} dealDelayMs={dealDelay(dealIndex, dealCount, i)} flip
        used={highlight?.used.has(c)} tilt={highlight?.tilt.get(c) ?? 0}
        onClick={picking ? () => onToggleCard(c) : undefined} disabled={!picking} />
    ));
  } else if (view.inHand) {
    // Another seat's cards: face-up when the snapshot carries them (the monitor's view), else backs. Once the seat folds its kept
    // cards leave the table (hidden in place, so the discard beside them stays put); after the hand, a seat's publicly shown
    // keepers (showdown, voluntary shows) flip face-up in their slots and the rest stay backs (or stay gone, if it folded).
    const known = view.holeCards !== null;
    const shown = known && live ? view.keepers ?? inHand(view) : null;
    const size = portrait ? 'md' : 'sm';
    const slotted = (i: number): Card | undefined => (slots.current.cards[i] !== undefined && view.shown.includes(slots.current.cards[i]!) ? slots.current.cards[i] : undefined);
    cards = shown
      ? shown.map((c, i) => <PlayingCard key={key(c)} card={c} size={size} dealDelayMs={known ? dealDelay(dealIndex, dealCount, i) : 0} flip used={highlight?.used.has(c)} tilt={highlight?.tilt.get(c) ?? 0} />)
      : !live || (showdown && view.shown.length > 0)
        ? Array.from({ length: 2 }, (_, i) => (slotted(i) !== undefined
            ? <PlayingCard key={key(i)} card={slotted(i)} size={size} reveal used={highlight?.used.has(slotted(i)!)} tilt={highlight?.tilt.get(slotted(i)!) ?? 0} />
            : <PlayingCard key={key(i)} faceDown size={size} className={live ? '' : 'gone'} />))
        : Array.from({ length: chosen ? 2 : 3 }, (_, i) => <PlayingCard key={key(i)} faceDown size={size} dealDelayMs={dealDelay(dealIndex, dealCount, i)} />);
  }
  const fromVars = dealFrom ? { ['--from-x' as string]: `${dealFrom.x}px`, ['--from-y' as string]: `${dealFrom.y}px` } : {};
  // The discard stays in the row beside the keepers, full size and tilted away, face-up to everyone once revealed; a locked, unrevealed one is a back.
  const size = isMe ? (portrait ? 'lg' : 'md') : (portrait ? 'md' : 'sm');
  const mucked = view.inHand && chosen   // a discard stays on the table after a fold, yours and everyone else's
    ? (view.discard !== null ? <PlayingCard key={key(view.discard)} card={view.discard} size={size} className="mucked" /> : <PlayingCard key={key('muck')} faceDown size={size} className="mucked" />)
    : null;
  const urgent = fraction !== null && fraction < 0.25;
  const extra = acting && extraTime;   // the clock is running on this player's time bank

  return (
    <div className={cls} data-testid={`seat-${view.seat}`}>
      {cards && (
        <div className={`seat-cards${isMe ? ' own' : ''}${liftedAny ? ' lifted' : ''}${liftedAll ? ' lifted-all' : ''}`} style={fromVars}>
          {cards}
          {mucked}
          {equity !== null && <span className="seat-equity">{equity}%</span>}
        </div>
      )}
      <span className="seat-plate" aria-hidden="true" />
      {cards && tags.length > 0 && <span className="hand-tags">{tags.map(t => <span key={t} className="hand-tag">{t}</span>)}</span>}
      <div className="seat-status">
        <span className={`avatar-ring${fraction !== null ? ' timing' : ''}${urgent ? ' urgent' : ''}${extra ? ' extra' : ''}`} style={fraction !== null ? { ['--frac' as string]: fraction } : undefined}
          role={fraction !== null ? 'timer' : undefined} aria-label={fraction !== null ? `${Math.ceil(remainingMs! / 1000)} seconds left` : undefined}>
          <span className="avatar" aria-hidden="true">{initials(view.nickname)}</span>
        </span>
        {caption && cards === null && <span className="caption">{caption}</span>}
        {caption && cards !== null && <span className="seat-flag">{caption}</span>}
        {extra && !caption && <span className="seat-flag extra">EXTRA TIME</span>}
      </div>
      <div className="seat-id">
        <span className="seat-name">{view.nickname}</span>
        <span className="seat-stack">{view.stack}{won !== null && <span className="seat-won">+{won}</span>}</span>
      </div>
      {view.isButton && <span className="dealer-disc" aria-label="dealer button">D</span>}   {/* in the pill's empty right end, so it always travels with the player */}
    </div>
  );
}
